<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TEMPLATES } from '@/lib/templates';
import type { Template, TemplateCategory } from '@/lib/templates';
import MxTemplateCard from '@/components/MxTemplateCard.vue';
import MxTemplateModal from '@/components/MxTemplateModal.vue';
import MxIcon from '@/components/MxIcon.vue';

type FilterId = 'all' | TemplateCategory;

const route = useRoute();
const router = useRouter();

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all',     label: 'Все' },
  { id: 'trends',  label: 'Тренды' },
  { id: 'classic', label: 'Классика' },
];

const activeFilter = ref<FilterId>('all');
const query = ref('');

const filtered = computed<Template[]>(() => {
  const q = query.value.trim().toLowerCase();
  return TEMPLATES.filter((t) => {
    if (activeFilter.value !== 'all' && t.category !== activeFilter.value) return false;
    if (!q) return true;
    return (
      t.title.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q)
    );
  });
});

const grouped = computed<{ id: TemplateCategory; title: string; items: Template[] }[]>(() => {
  const sections: { id: TemplateCategory; title: string }[] = [
    { id: 'trends',  title: 'Тренды' },
    { id: 'classic', title: 'Классика' },
  ];
  return sections
    .map((s) => ({ ...s, items: filtered.value.filter((t) => t.category === s.id) }))
    .filter((s) => s.items.length > 0);
});

const selected = ref<Template | null>(null);
const modalOpen = ref(false);

function open(t: Template) {
  selected.value = t;
  modalOpen.value = true;
}

function go(t: Template) {
  modalOpen.value = false;
  router.push(`/create/${t.kind}?template=${t.id}`);
}

function clearSearch() { query.value = ''; }
function resetFilters() {
  query.value = '';
  activeFilter.value = 'all';
}

onMounted(async () => {
  const cat = route.query.cat;
  if (typeof cat === 'string' && (cat === 'trends' || cat === 'classic')) {
    activeFilter.value = cat;
    await nextTick();
    document.getElementById(`sect-${cat}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});
</script>

<template>
  <section class="ideas">
    <h1 class="ideas__h1">Идеи</h1>

    <div class="ideas__search">
      <MxIcon name="Search" :size="18" class="ideas__search-icon" />
      <input
        v-model="query"
        type="text"
        class="ideas__input"
        placeholder="Поиск шаблонов"
        aria-label="Поиск шаблонов"
        autocomplete="off"
      />
      <button v-if="query" class="ideas__clear" @click="clearSearch" aria-label="Очистить">
        <MxIcon name="X" :size="16" />
      </button>
    </div>

    <div class="ideas__chips">
      <button
        v-for="f in FILTERS"
        :key="f.id"
        :class="['ideas__chip', { 'ideas__chip--active': activeFilter === f.id }]"
        :aria-pressed="activeFilter === f.id"
        @click="activeFilter = f.id"
      >
        {{ f.label }}
      </button>
    </div>

    <div v-if="grouped.length === 0" class="ideas__empty">
      <p class="ideas__empty-title">Ничего не нашлось</p>
      <p class="ideas__empty-hint">Попробуйте другое слово или сбросьте фильтр.</p>
      <button class="ideas__empty-reset" data-test="ideas-reset" @click="resetFilters">
        Сбросить фильтры
      </button>
    </div>

    <div v-for="s in grouped" :key="s.id" :id="`sect-${s.id}`" class="ideas__sect">
      <h2 class="ideas__h2">{{ s.title }}</h2>
      <div class="ideas__grid">
        <MxTemplateCard
          v-for="t in s.items"
          :key="t.id"
          :template="t"
          @select="open"
        />
      </div>
    </div>

    <MxTemplateModal v-model:open="modalOpen" :template="selected" @select="go" />
  </section>
</template>

<style scoped>
.ideas { padding: var(--sp-2) 0 120px; }
.ideas__h1 {
  font-family: var(--font-display);
  font-size: var(--fs-2xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-bottom: var(--sp-4);
}
.ideas__search {
  position: relative;
  margin-bottom: var(--sp-3);
}
.ideas__search-icon {
  position: absolute;
  left: var(--sp-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
  pointer-events: none;
}
.ideas__input {
  width: 100%;
  padding: 10px var(--sp-3) 10px calc(var(--sp-3) + 22px);
  border-radius: var(--r-md);
  background: var(--bg-elevated);
  border: 1px solid transparent;
  color: var(--text-primary);
  font-size: var(--fs-md);
  outline: none;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.ideas__input:focus-visible {
  border-color: var(--accent);
  background: var(--bg-base);
}
.ideas__input::placeholder { color: var(--text-tertiary); }
.ideas__clear {
  position: absolute;
  right: var(--sp-2);
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: var(--text-tertiary);
  background: var(--bg-overlay);
  -webkit-tap-highlight-color: transparent;
}
.ideas__clear:active { transform: translateY(-50%) scale(0.94); }
.ideas__chips {
  display: flex;
  gap: var(--sp-2);
  overflow-x: auto;
  scrollbar-width: none;
  margin-bottom: var(--sp-5);
  padding-bottom: 4px;
}
.ideas__chips::-webkit-scrollbar { display: none; }
.ideas__chip {
  flex: 0 0 auto;
  padding: 6px 14px;
  border-radius: 999px;
  background: var(--bg-elevated);
  color: var(--text-secondary);
  font-size: var(--fs-sm);
  font-weight: 500;
  border: 1px solid transparent;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}
.ideas__chip--active {
  background: var(--accent-soft);
  color: var(--accent);
  border-color: var(--accent);
}
.ideas__sect { margin-bottom: var(--sp-6); }
.ideas__h2 {
  font-size: var(--fs-sm);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
  margin-bottom: var(--sp-3);
}
.ideas__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
.ideas__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-8) var(--sp-3);
  text-align: center;
}
.ideas__empty-title {
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}
.ideas__empty-hint {
  color: var(--text-tertiary);
  font-size: var(--fs-sm);
  max-width: 28ch;
  margin-bottom: var(--sp-2);
}
.ideas__empty-reset {
  padding: 10px 18px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: var(--fs-sm);
  font-weight: 600;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease;
}
.ideas__empty-reset:active { transform: scale(0.96); }
</style>
